import { useCallback, useEffect, useState } from "react";

import { ApiFailure, api, type ApiKey } from "../lib/api";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Skeleton,
  inputClass,
  relativeTime,
} from "../components/ui";

interface ScopeInfo {
  scope: string;
  description: string;
}

const TONE = { active: "ok", expired: "warn", revoked: "danger" } as const;

export function Keys() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [scopes, setScopes] = useState<ScopeInfo[]>([]);
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<{ token: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, available] = await Promise.all([
        api.get<ApiKey[]>("/keys"),
        api.get<ScopeInfo[]>("/keys/scopes"),
      ]);
      setKeys(list);
      setScopes(available);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not load keys.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">API keys</h1>
          <p className="mt-1 text-[13px] text-[var(--muted)]">
            Credentials for calling the API. Each one carries only the scopes you grant it.
          </p>
        </div>
        {!creating && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            Issue a key
          </Button>
        )}
      </header>

      {error && <ErrorNote>{error}</ErrorNote>}

      {/* Shown once, and unrecoverable afterwards. Given its own prominent card
          rather than a toast, because a toast that disappears after four
          seconds is how someone loses a key they cannot get back. */}
      {issued && (
        <Card className="animate-rise border-[var(--brand)]/40 bg-[var(--brand-soft)]">
          <div className="text-[15px] font-semibold text-[var(--brand)]">
            Copy this now — it will not be shown again
          </div>
          <p className="mt-1 text-[13px] text-[var(--muted)]">
            Only a hash of “{issued.name}” is stored. If you lose this token, issue a new key.
          </p>
          <div className="mt-3 flex gap-2">
            <code className="flex-1 overflow-x-auto rounded-[10px] border border-[var(--border)] bg-[var(--background)] px-3 py-2.5 font-mono text-[13px]">
              {issued.token}
            </code>
            <Button onClick={() => void navigator.clipboard.writeText(issued.token)}>Copy</Button>
            <Button variant="ghost" onClick={() => setIssued(null)}>
              Done
            </Button>
          </div>
        </Card>
      )}

      {creating && (
        <CreateKey
          scopes={scopes}
          onCancel={() => setCreating(false)}
          onCreated={(token, name) => {
            setIssued({ token, name });
            setCreating(false);
            void load();
          }}
        />
      )}

      {keys === null ? (
        <div className="grid gap-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : keys.length === 0 ? (
        <Card padded={false}>
          <Empty
            title="No API keys yet"
            body="Issue one to let another system read proposals, submit them, or subscribe to events."
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                Issue a key
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-3">
          {keys.map((k) => (
            <Card key={k.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[15px] font-medium">{k.name}</span>
                    <Badge tone={TONE[k.status]}>{k.status}</Badge>
                    {k.environment === "test" && <Badge tone="neutral">test</Badge>}
                  </div>
                  <div className="mt-1.5 font-mono text-[12px] text-[var(--faint)]">
                    {k.displayPrefix}…{k.last4}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {k.scopes.map((s) => (
                      <Badge key={s} tone="neutral">
                        {s}
                      </Badge>
                    ))}
                  </div>
                  <div className="tabular mt-2.5 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[var(--faint)]">
                    <span>{k.requestCount.toLocaleString()} requests</span>
                    <span>·</span>
                    <span>last used {relativeTime(k.lastUsedAt)}</span>
                    <span>·</span>
                    <span>created {relativeTime(k.createdAt)}</span>
                  </div>
                </div>

                {k.status === "active" && (
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (!confirm(`Revoke “${k.name}”? This takes effect immediately and cannot be undone.`)) return;
                      void api
                        .delete(`/keys/${k.id}`)
                        .then(load)
                        .catch((err: unknown) =>
                          setError(err instanceof ApiFailure ? err.error.message : "Could not revoke."),
                        );
                    }}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateKey({
  scopes,
  onCancel,
  onCreated,
}: {
  scopes: ScopeInfo[];
  onCancel: () => void;
  onCreated: (token: string, name: string) => void;
}) {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.post<{ token: string }>("/keys", { name, scopes: selected });
      onCreated(result.token, name);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "Could not issue the key.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="animate-rise">
      <h2 className="text-[15px] font-semibold">Issue a key</h2>
      <div className="mt-4 grid gap-4">
        <Field label="Name" hint="What is this key for? You will see it in the list and the audit log.">
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="CI pipeline"
            maxLength={80}
            autoFocus
          />
        </Field>

        <div>
          <div className="mb-1.5 text-[13px] font-medium">Scopes</div>
          <p className="mb-2.5 text-[12px] text-[var(--faint)]">
            Grant the least it needs. Issuing keys, managing users and changing settings can never be
            done with a key, so they are not listed here.
          </p>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {scopes.map((s) => (
              <label
                key={s.scope}
                className="flex cursor-pointer items-start gap-2.5 rounded-[10px] border border-[var(--border)] bg-[var(--surface-2)] p-2.5 transition-colors duration-150 hover:border-[var(--border-strong)]"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 accent-[var(--brand)]"
                  checked={selected.includes(s.scope)}
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.target.checked ? [...prev, s.scope] : prev.filter((x) => x !== s.scope),
                    )
                  }
                />
                <span className="min-w-0">
                  <span className="block font-mono text-[12px]">{s.scope}</span>
                  <span className="mt-0.5 block text-[12px] leading-snug text-[var(--faint)]">
                    {s.description}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={() => void create()}
            disabled={busy || !name.trim() || selected.length === 0}
          >
            {busy ? "Issuing…" : "Issue key"}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
